/**
 * Header search drawer. Open/close is a <dialog>; typing fetches
 * sections/predictive-results.liquid. Tabs appear once a query is typed.
 */

import {isStrictQuery, looseQuery, strictQuery} from './component-search-query.js'; // eslint-disable-line import/extensions

const MIN_QUERY_LENGTH = 2;
const COUNT_TOKEN = '%COUNT%';
const TERMS_TOKEN = '%TERMS%';

/* Searchanise binds keydown and `search` on every search input and sends
   Enter to /pages/search-results-page. Window capture runs before any
   listener on the document or the input, so these handlers stop the app's
   Enter and submit handling and send the form to routes.search_url with its
   own fields (q, type, options[prefix]). input, paste, focus and click are
   left alone: stopping them here would also stop the drawer's live results,
   and the app's autocomplete they drive is hidden in section-search-drawer.css. */
const SEARCH_FORM = '[data-search-drawer-form], [data-search-form], .editorial-masthead__search-form';
const ENTER_EVENTS = ['keydown', 'keypress', 'keyup'];

if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends HTMLElement {
      constructor() {
        super();
        this.abortController = undefined;
        this.debounceTimer = undefined;
        this.lastOpener = undefined;
        this.activeTab = 'all';
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onClear = this.onClear.bind(this);
        this.onTabClick = this.onTabClick.bind(this);
        this.onDialogClick = this.onDialogClick.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onSearchKey = this.onSearchKey.bind(this);
        this.onSearchEvent = this.onSearchEvent.bind(this);
        this.onSearchSubmit = this.onSearchSubmit.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('[data-search-drawer-form]');
        this.input = this.querySelector('[data-search-drawer-input]');
        this.clearButton = this.querySelector('[data-search-drawer-clear]');
        this.empty = this.querySelector('[data-drawer-empty]');
        this.live = this.querySelector('[data-drawer-live]');
        this.tabs = this.querySelector('[data-search-drawer-tabs]');
        this.meta = this.querySelector('[data-search-drawer-meta]');
        this.metaTerms = this.querySelector('[data-search-drawer-terms]');
        this.metaCount = this.querySelector('[data-search-drawer-count]');
        this.railSearchesTitle = this.querySelector('[data-search-drawer-rail-searches]');
        this.footer = this.querySelector('[data-search-drawer-footer]');
        this.viewAll = this.querySelector('[data-search-drawer-view-all]');
        this.suggestUrl = this.dataset.suggestUrl;
        this.searchUrl = this.dataset.searchUrl;
        this.searchTypes = this.dataset.searchTypes;
        this.countTypes = this.searchTypes.split(',').filter(Boolean);
        this.liveDelay = Number(this.dataset.liveDelay);

        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('resize', this.onResize);
        for (const type of ENTER_EVENTS) {
          window.addEventListener(type, this.onSearchKey, true);
        }
        window.addEventListener('search', this.onSearchEvent, true);
        window.addEventListener('submit', this.onSearchSubmit, true);
        this.input?.addEventListener('input', this.onInput);
        this.clearButton?.addEventListener('click', this.onClear);
        this.tabs?.addEventListener('click', this.onTabClick);
        this.dialog?.addEventListener('click', this.onDialogClick);
        this.dialog?.addEventListener('close', this.onDialogClose);
        this.syncClearButton();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onResize);
        for (const type of ENTER_EVENTS) {
          window.removeEventListener(type, this.onSearchKey, true);
        }
        window.removeEventListener('search', this.onSearchEvent, true);
        window.removeEventListener('submit', this.onSearchSubmit, true);
        this.input?.removeEventListener('input', this.onInput);
        this.clearButton?.removeEventListener('click', this.onClear);
        this.tabs?.removeEventListener('click', this.onTabClick);
        this.dialog?.removeEventListener('click', this.onDialogClick);
        this.dialog?.removeEventListener('close', this.onDialogClose);
        clearTimeout(this.debounceTimer);
        this.abortController?.abort();
      }

      onResize() {
        if (this.dialog?.open) {
          this.syncOffset();
        }
      }

      /**
       * @param {EventTarget | null} target
       * @returns {HTMLFormElement | null} The theme search form the target sits in
       */
      searchFormFor(target) {
        if (!(target instanceof Element)) {
          return null;
        }

        const form = target.closest('form');
        if (!form) {
          return null;
        }

        const rewritten = (form.getAttribute('action') || '').includes('search-results-page');
        return rewritten || form.matches(SEARCH_FORM) ? form : null;
      }

      /**
       * @param {HTMLFormElement} form
       */
      goToSearch(form) {
        const target = new URL(this.searchUrl || '/search', window.location.origin);
        for (const [key, value] of new FormData(form).entries()) {
          if (typeof value === 'string') {
            target.searchParams.append(key, key === 'q' ? this.queryFor(value) : value);
          }
        }

        window.location.assign(target);
      }

      /**
       * @param {KeyboardEvent} event
       */
      onSearchKey(event) {
        if (event.key !== 'Enter' || event.isComposing) {
          return;
        }

        const form = this.searchFormFor(event.target);
        if (!form) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        if (event.type === 'keydown') {
          this.goToSearch(form);
        }
      }

      /**
       * @param {Event} event
       */
      onSearchEvent(event) {
        if (this.searchFormFor(event.target)) {
          event.stopImmediatePropagation();
        }
      }

      /**
       * @param {SubmitEvent} event
       */
      onSearchSubmit(event) {
        const form = this.searchFormFor(event.target);
        if (!form) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.goToSearch(form);
      }

      syncOffset() {
        const floating = document.getElementById('floating-main-header');
        const header = floating?.classList.contains('is-visible') ? floating : document.getElementById('main-header');
        const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
        this.dialog?.style.setProperty('--search-drawer-offset', `${Math.max(bottom, 0)}px`);
      }

      onDocumentClick(event) {
        const opener = event.target.closest('[data-search-drawer-open]');
        if (opener) {
          event.preventDefault();
          if (this.dialog?.open) {
            this.close();
            return;
          }
          this.open(opener);
          return;
        }

        const termLink = event.target.closest('[data-search-drawer-term]');
        if (termLink && this.contains(termLink)) {
          event.preventDefault();
          this.fillQuery(termLink.dataset.searchDrawerTerm);
        }
      }

      onDialogClick(event) {
        if (event.target === this.dialog) {
          this.close();
        }
      }

      onDialogClose() {
        this.syncOpeners(false);
        this.lastOpener?.focus();
      }

      open(opener) {
        this.lastOpener = opener;
        this.syncOffset();
        this.dialog?.showModal();
        this.syncOpeners(true);
        this.input?.focus();
      }

      close() {
        this.dialog?.close();
      }

      syncOpeners(isOpen) {
        for (const opener of document.querySelectorAll('[data-search-drawer-open]')) {
          opener.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
      }

      onTabClick(event) {
        const tab = event.target.closest('[data-drawer-tab]');
        if (!tab) {
          return;
        }
        this.setTab(tab.dataset.drawerTab);
      }

      setTab(tab) {
        this.activeTab = tab;
        for (const button of this.tabs?.querySelectorAll('[data-drawer-tab]') || []) {
          if (button.dataset.drawerTab === tab) {
            button.classList.add('search__tab--active');
            button.setAttribute('aria-selected', 'true');
          } else {
            button.classList.remove('search__tab--active');
            button.setAttribute('aria-selected', 'false');
          }
        }
        this.applyTabFilter();
      }

      applyTabFilter() {
        for (const group of this.querySelectorAll('[data-drawer-group]')) {
          if (this.activeTab === 'all' || group.dataset.drawerGroup === this.activeTab) {
            group.hidden = false;
          } else {
            group.hidden = true;
          }
        }
      }

      syncFooter(terms, hasResults, resultCount) {
        if (!this.footer || !this.viewAll) {
          return;
        }

        if (!hasResults || !terms) {
          this.clearFooter();
          return;
        }

        const target = new URL(this.searchUrl, window.location.origin);
        target.searchParams.set('q', this.liveQuery || this.queryFor(terms));
        target.searchParams.set('options[prefix]', 'last');
        target.searchParams.set('type', this.searchTypes);
        this.viewAll.href = target.toString();

        const count = this.resolveViewAllCount(resultCount);
        const template = this.viewAll.dataset.labelTemplate || '';
        this.viewAll.textContent = template.replace(COUNT_TOKEN, String(count ?? '')).replace(TERMS_TOKEN, terms);
        this.footer.hidden = false;
      }

      /**
       * View all opens /search. Prefer the exact product total from ?view=count
       * so the label matches the search page, not the suggest preview.
       */
      resolveViewAllCount(productCount) {
        if (typeof productCount === 'number' && productCount > 0) {
          return productCount;
        }

        const source = this.live?.querySelector('[data-drawer-counts]');
        if (!source) {
          return productCount;
        }

        let total = 0;
        let found = false;
        for (const type of this.countTypes) {
          const value = Number.parseInt(source.dataset[type], 10);
          if (!Number.isNaN(value)) {
            total += value;
            found = true;
          }
        }

        return found ? total : productCount;
      }

      clearFooter() {
        if (this.footer) {
          this.footer.hidden = true;
        }
      }

      onInput() {
        const terms = this.input.value.trim();
        this.syncClearButton();

        clearTimeout(this.debounceTimer);

        if (terms.length < MIN_QUERY_LENGTH) {
          this.abortController?.abort();
          this.showEmpty();
          return;
        }

        this.debounceTimer = setTimeout(() => {
          this.fetchResults(terms);
        }, this.liveDelay);
      }

      onClear() {
        if (!this.input) {
          return;
        }

        this.input.value = '';
        this.input.focus();
        clearTimeout(this.debounceTimer);
        this.abortController?.abort();
        this.syncClearButton();
        this.showEmpty();
      }

      syncClearButton() {
        if (!this.clearButton || !this.input) {
          return;
        }

        this.clearButton.toggleAttribute('hidden', this.input.value.length === 0);
      }

      fillQuery(term) {
        if (!this.input) {
          return;
        }
        this.input.value = term;
        this.syncClearButton();
        this.fetchResults(term.trim());
      }

      showEmpty() {
        this.empty.hidden = false;
        this.live.hidden = true;
        this.live.innerHTML = '';
        this.clearFooter();
        this.clearLiveChrome();
        this.setTab('all');
      }

      syncLiveChrome(terms, productCount) {
        if (this.meta) {
          this.meta.hidden = false;
        }

        if (this.metaTerms) {
          this.metaTerms.textContent = `\u201C${terms}\u201D`;
        }

        if (this.metaCount) {
          if (productCount === undefined || productCount === null) {
            this.metaCount.hidden = true;
          } else {
            const template = this.metaCount.dataset.countTemplate || '';
            this.metaCount.textContent = template.replace(COUNT_TOKEN, String(productCount));
            this.metaCount.hidden = false;
          }
        }

        if (this.railSearchesTitle) {
          const template = this.railSearchesTitle.dataset.forTemplate || '';
          this.railSearchesTitle.textContent = template.replace(TERMS_TOKEN, terms);
        }

        this.syncProductHeading(productCount);
      }

      clearLiveChrome() {
        if (this.meta) {
          this.meta.hidden = true;
        }

        if (this.metaCount) {
          this.metaCount.hidden = true;
          this.metaCount.textContent = '';
        }

        if (this.railSearchesTitle) {
          this.railSearchesTitle.textContent = this.railSearchesTitle.dataset.idle || '';
        }

        this.clearTabCounts();
      }

      clearTabCounts() {
        for (const slot of this.querySelectorAll('[data-count-for]')) {
          slot.hidden = true;
          slot.textContent = '';
        }
      }

      async fetchProductCount(query, signal) {
        const target = new URL(this.searchUrl, window.location.origin);
        target.searchParams.set('q', query);
        target.searchParams.set('type', 'product');
        target.searchParams.set('options[prefix]', 'last');
        target.searchParams.set('view', 'count');

        try {
          const requestOptions = {
            signal,
          };
          const response = await fetch(target.toString(), requestOptions);
          if (!response.ok) {
            return undefined;
          }

          const count = Number.parseInt((await response.text()).trim(), 10);
          if (Number.isNaN(count)) {
            return undefined;
          }

          return count;
        } catch (error) {
          if (error.name === 'AbortError') {
            throw error;
          }

          return undefined;
        }
      }

      async fetchResults(terms) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.dialog?.setAttribute('aria-busy', 'true');

        try {
          /* Quoted words first, so only real matches come back
             (assets/component-search-query.js); a quoted query that finds no
             products is retried as typed, which keeps typo tolerance. */
          let query = this.queryFor(terms);
          let {response, productCount} = await this.requestResults(query, this.abortController.signal);
          if (productCount === 0 && isStrictQuery(query)) {
            query = looseQuery(query);
            ({response, productCount} = await this.requestResults(query, this.abortController.signal));
          }
          if (!response.ok) {
            throw new Error(`Search suggest failed: ${response.status}`);
          }

          if (this.input.value.trim() !== terms) {
            return;
          }

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = html.querySelector('[data-drawer-live]') || html.querySelector('#shopify-section-predictive-results');
          this.live.innerHTML = next ? next.innerHTML : '';
          this.applyProductLimit();
          this.empty.hidden = true;
          this.live.hidden = false;
          this.liveQuery = query;
          this.syncFooter(terms, Boolean(this.live.querySelector('[data-drawer-group]')), productCount);
          this.syncLiveChrome(terms, productCount);
          this.syncCounts(html, productCount);
          this.applyTabFilter();
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }
          window.location.assign(`${this.searchUrl}?q=${encodeURIComponent(this.queryFor(terms))}&type=${encodeURIComponent(this.searchTypes)}`);
        } finally {
          this.dialog?.removeAttribute('aria-busy');
        }
      }

      /**
       * Theme settings → Search → Matching. Keyword (the default) quotes each
       * word; Broad sends the words as typed and lets Shopify add related
       * products.
       * @param {string} terms
       * @returns {string}
       */
      queryFor(terms) {
        return this.dataset.searchMode === 'broad' ? terms : strictQuery(terms);
      }

      /**
       * One suggest request plus the exact product total for the same query.
       * @param {string} query
       * @param {AbortSignal} signal
       * @returns {Promise<{response: Response, productCount: number | undefined}>}
       */
      async requestResults(query, signal) {
        const target = new URL(this.suggestUrl, window.location.origin);
        target.searchParams.set('q', query);
        target.searchParams.set('section_id', 'predictive-results');
        target.searchParams.set('resources[type]', this.searchTypes);
        target.searchParams.set('resources[limit]', '10');
        target.searchParams.set('resources[limit_scope]', 'each');
        target.searchParams.set('options[prefix]', 'last');

        const [response, productCount] = await Promise.all([
          fetch(target.toString(), {signal}),
          this.countTypes.includes('product') ? this.fetchProductCount(query, signal) : Promise.resolve(undefined),
        ]);

        return {response, productCount};
      }

      applyProductLimit() {
        const limit = Number(this.dataset.productsLimit);
        if (!Number.isFinite(limit) || limit < 1) {
          return;
        }

        const group = this.live.querySelector('[data-drawer-group="product"]');
        const row = group?.querySelector('.search__rows');
        if (!row) {
          return;
        }

        const extras = [...row.children].slice(limit);
        for (const item of extras) {
          item.remove();
        }
      }

      syncProductHeading(productCount) {
        if (productCount === undefined || productCount === null) {
          return;
        }

        const group = this.live.querySelector('[data-drawer-group="product"]');
        if (!group) {
          return;
        }

        const heading = group.querySelector('[data-product-heading-template]');
        const headingTemplate = heading?.dataset.productHeadingTemplate;
        if (heading && headingTemplate) {
          heading.textContent = headingTemplate.replace(COUNT_TOKEN, String(productCount));
        }

        const count = group.querySelector('[data-product-count-template]');
        const countTemplate = count?.dataset.productCountTemplate;
        if (count && countTemplate) {
          count.textContent = countTemplate.replace(COUNT_TOKEN, String(productCount));
        }
      }

      syncCounts(html, productCount) {
        const source = html.querySelector('[data-drawer-counts]');

        for (const type of this.countTypes) {
          let total = source?.dataset[type];
          if (type === 'product' && productCount !== undefined && productCount !== null) {
            total = String(productCount);
          }
          const slot = this.querySelector(`[data-count-for="${type}"]`);
          if (!slot) {
            continue;
          }
          if (total === undefined || total === '') {
            slot.hidden = true;
            continue;
          }
          slot.textContent = `(${total})`;
          slot.hidden = false;
        }
      }
    },
  );
}
